import { useState, useRef } from "react"
import styled from "styled-components"
import { Icon_Sliders } from "../../styles/icons"
import useScreenSize from "../../hooks/useScreenSize"
import { useModalStack } from "../../context/modalStackContext"
import RadioModal from "../select/RadioModal"
import useTranslation from "../../hooks/useTranslation"

const CompactText = styled.button`
  appearance: none;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  font-size: inherit;
  color: ${({ theme }) => theme.font.color};
  text-decoration: underline;
  text-underline-offset: 0.2em;
  text-align: left;

  &:hover {
    color: ${({ theme }) => theme.primary?.active || theme.font.active};
  }
`

const CompactClear = styled.button`
  appearance: none;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.font.color};
  cursor: pointer;
  padding: 0 0 0 0.5rem;
  font-size: var(--font-XXS);
  text-decoration: underline;
  text-underline-offset: 0.2em;

  &:hover {
    color: ${({ theme }) => theme.secondary?.active || theme.font.active};
  }
`

const FilterButton = styled.button`
  border-radius: 2rem;
  background-color: transparent;
  border: 1px solid ${({ theme }) => theme.area.border};
  color: ${({ theme }) => theme.font.color};
  margin-right: 0;
  padding-inline: 0.75rem;
  padding-block: 0rem;
  font-size: var(--font-XS);
  height: 40px; /* level with the group filter selectors (DropDown.Toggle) */
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;

  &:hover {
    color: ${({ theme }) => theme.primary?.active || theme.font.active};
  }
`

const IconWrapper = styled.div`
  margin-right: ${({ $ismobile }) => ($ismobile ? "0rem" : "0.25rem")};
  width: 1rem;
  height: 1rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`

const Tag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  border: 1px solid ${({ theme }) => theme.area.border};
  color: ${({ theme }) => theme.font.color};
  background: ${({ theme }) => theme.page?.background || theme.table?.header || "transparent"};
  padding: 0 0.5rem;
  height: 40px;
  border-radius: 2rem;
  font-size: ${({ $ismobile }) => ($ismobile ? "var(--font-XXS)" : "var(--font-XS)")};

`

const TagLabel = styled.span`
  max-width: 16rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const TagClear = styled.button`
  appearance: none;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.font.color};
  cursor: pointer;
  font-size: 0.9rem;
  line-height: 1;
  padding: 0 0.25rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: ${({ theme }) => theme.secondary?.active || theme.font.active};
  }
`

const TagContainer = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
`

const RemoveText = styled.button`
  appearance: none;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.font.color};
  cursor: pointer;
  margin-top: 0.25rem;
  padding: 0 0.25rem 0 0;
  font-size: var(--font-XXS);
  line-height: 1;
  text-decoration: underline;
  display: block;
  width: 100%;
  text-align: right;

  &:hover {
    color: ${({ theme }) => theme.secondary?.active || theme.font.active};
  }
`

/**
 * FilterControl
 * Props:
 * - config: filter config object (expects at least modalTitle, entity, modalType==='radioGroup', radioGroups, persistValue, options)
 * - selectedLabel: string label for active filter (if present, show tag instead of button)
 * - onApply(locationData): callback from modal selection
 * - onClear(): clear active filter
 */
const FilterControl = ({ config, selectedLabel, onApply, onClear }) => {
  const { isMobile } = useScreenSize()
  const { pushModal } = useModalStack()
  const { t } = useTranslation()
  const [isVisible, setIsVisible] = useState(false)
  const modalIdRef = useRef(null)

  const handleOpen = () => {
    if (!config) return
    const {
      modalTitle,
      modalOpacity = 0.5,
      entity = "location",
      radioGroups = [],
      persistValue = true,
      options,
    } = config

    modalIdRef.current = pushModal(RadioModal, {
      isVisible: true,
      setIsVisible: setIsVisible,
      data: null,
      radioGroups,
      valueName: selectedLabel || "",
      onSelect: (locationData) => {
        if (onApply) onApply(locationData)
      },
      entity,
      menuItemField: "name",
      modalTitle,
      modalOpacity,
      options,
      persistValue,
      testId: `${entity}.filter`,
    })

    setIsVisible(true)
  }

  const compactOnMobile = config?.compactOnMobile

  if (compactOnMobile && isMobile) {
    return (
      <span>
        <CompactText onClick={handleOpen} data-testid="filter.open.compact">
          {selectedLabel || (config?.placeholder ? (t ? t(config.placeholder) : config.placeholder) : t ? t('filter') : 'Filter')}
        </CompactText>
        {selectedLabel && onClear && (
          <CompactClear onClick={onClear} data-testid="filter.clear.compact">
            ×
          </CompactClear>
        )}
      </span>
    )
  }

  if (selectedLabel) {
    if (isMobile) {
      return (
        <TagContainer data-testid="filter.tag.container">
          <Tag data-testid="filter.tag" $ismobile={isMobile}>
            <TagLabel>{selectedLabel}</TagLabel>
          </Tag>
          <RemoveText onClick={onClear} data-testid="filter.clear.text">
            {config?.mobileText ? (t ? t(config.mobileText) : config.mobileText) : 'remove'}
          </RemoveText>
        </TagContainer>
      )
    }
    return (
      <Tag data-testid="filter.tag" $ismobile={isMobile}>
        <TagLabel>{selectedLabel}</TagLabel>
        <TagClear onClick={onClear} aria-label="Clear filter" data-testid="filter.clear">
          ×
        </TagClear>
      </Tag>
    )
  }

  return (
    <FilterButton onClick={handleOpen} data-testid="filter.open">
      <IconWrapper $ismobile={isMobile}>{Icon_Sliders}</IconWrapper>
      {!isMobile && "Filter"}
    </FilterButton>
  )
}

export default FilterControl
